import { useState, useCallback, useRef, useEffect } from 'react';
import './FaceCropModal.css';

type Props = {
  imageFile: File;
  onConfirm: (croppedBase64: string) => void;
  onCancel: () => void;
};

type CropArea = {
  x: number;
  y: number;
  size: number; // 正方形なので size のみ
};

export function FaceCropModal({ imageFile, onConfirm, onCancel }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [cropArea, setCropArea] = useState<CropArea>({ x: 0.25, y: 0.1, size: 0.5 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragType, setDragType] = useState<'move' | 'resize' | null>(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [imageDataUrl, setImageDataUrl] = useState<string>('');

  // ファイルを読み込んでDataURLに変換
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImageDataUrl(e.target.result as string);
      }
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // 画像をロードして表示
  useEffect(() => {
    if (!imageDataUrl) return;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;

      // 表示サイズを計算（コンテナに収まるように）
      const container = containerRef.current;
      if (container) {
        const maxWidth = Math.min(container.clientWidth - 40, 500);
        const maxHeight = 400;
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        setDisplaySize({
          width: img.width * ratio,
          height: img.height * ratio,
        });
      }

      // 初期クロップ領域を中央に設定
      const initialSize = Math.min(0.6, 0.6 * (img.width / img.height));
      setCropArea({
        x: (1 - initialSize) / 2,
        y: (1 - initialSize * (img.width / img.height)) / 2,
        size: initialSize,
      });
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  // キャンバスを描画
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || displaySize.width === 0) return;

    canvas.width = displaySize.width;
    canvas.height = displaySize.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 画像を描画
    ctx.drawImage(img, 0, 0, displaySize.width, displaySize.height);

    // オーバーレイを描画（暗くする）
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, displaySize.width, displaySize.height);

    // クロップ領域（正円）
    const cropX = cropArea.x * displaySize.width;
    const cropY = cropArea.y * displaySize.height;
    const cropSize = cropArea.size * displaySize.width;
    const centerX = cropX + cropSize / 2;
    const centerY = cropY + cropSize / 2;
    const radius = cropSize / 2;

    // 円形の領域をクリア
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // クロップ領域の元画像を円形で再描画
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, 0, 0, displaySize.width, displaySize.height);
    ctx.restore();

    // クロップ領域の枠線（円形）- シルバー色
    ctx.strokeStyle = 'rgba(192, 192, 192, 0.9)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    // リサイズハンドル（右下）
    const handleSize = 12;
    const handleX = centerX + radius * Math.cos(Math.PI / 4);
    const handleY = centerY + radius * Math.sin(Math.PI / 4);
    ctx.fillStyle = 'rgba(192, 192, 192, 0.9)';
    ctx.beginPath();
    ctx.arc(handleX, handleY, handleSize / 2, 0, Math.PI * 2);
    ctx.fill();
  }, [cropArea, displaySize]);

  // マウスダウン
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / displaySize.width;
    const y = (e.clientY - rect.top) / displaySize.height;

    // リサイズハンドルのクリック判定
    const centerX = cropArea.x + cropArea.size / 2;
    const centerY = cropArea.y + (cropArea.size * displaySize.width) / displaySize.height / 2;
    const handleX = centerX + (cropArea.size / 2) * Math.cos(Math.PI / 4);
    const handleY = centerY + ((cropArea.size * displaySize.width) / displaySize.height / 2) * Math.sin(Math.PI / 4);

    const distToHandle = Math.sqrt(Math.pow(x - handleX, 2) + Math.pow(y - handleY, 2));
    if (distToHandle < 0.03) {
      setIsDragging(true);
      setDragType('resize');
      setDragStart({ x, y });
      return;
    }

    // クロップ領域内のクリック判定（円形）
    const distToCenter = Math.sqrt(
      Math.pow(x - centerX, 2) +
      Math.pow((y - centerY) * (displaySize.height / displaySize.width), 2)
    );
    if (distToCenter < cropArea.size / 2) {
      setIsDragging(true);
      setDragType('move');
      setDragStart({ x: x - cropArea.x, y: y - cropArea.y });
    }
  }, [cropArea, displaySize]);

  // マウスムーブ
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / displaySize.width;
    const y = (e.clientY - rect.top) / displaySize.height;

    if (dragType === 'move') {
      const cropHeightNorm = (cropArea.size * displaySize.width) / displaySize.height;
      const newX = Math.max(0, Math.min(1 - cropArea.size, x - dragStart.x));
      const newY = Math.max(0, Math.min(1 - cropHeightNorm, y - dragStart.y));
      setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
    } else if (dragType === 'resize') {
      const centerX = cropArea.x + cropArea.size / 2;
      const centerY = cropArea.y + (cropArea.size * displaySize.width) / displaySize.height / 2;

      // 中心からマウスまでの距離でサイズを決定
      const dist = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)) * Math.sqrt(2);
      const newSize = Math.max(0.1, Math.min(1, dist));

      // 新しい位置を計算（中心を維持）
      const newX = centerX - newSize / 2;
      const newHeightNorm = (newSize * displaySize.width) / displaySize.height;
      const newY = centerY - newHeightNorm / 2;

      // 範囲チェック
      if (newX >= 0 && newX + newSize <= 1 && newY >= 0 && newY + newHeightNorm <= 1) {
        setCropArea({ x: newX, y: newY, size: newSize });
      }
    }
  }, [isDragging, dragType, dragStart, cropArea, displaySize]);

  // マウスアップ
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
  }, []);

  // クロップを適用して顔画像を生成（圧縮付き）
  const handleConfirm = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    // クロップサイズを計算
    const cropSizePx = Math.round(cropArea.size * img.width);

    // 出力サイズを制限（プロフィール画像なので256pxで十分）
    const maxOutputSize = 256;
    const outputSize = Math.min(cropSizePx, maxOutputSize);

    // オフスクリーンキャンバスでクロップ
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = outputSize;
    cropCanvas.height = outputSize;

    const ctx = cropCanvas.getContext('2d');
    if (!ctx) return;

    // 高品質なリサイズのための設定
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 円形にクリップ
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();

    // 画像を描画（リサイズ込み）
    const srcX = Math.round(cropArea.x * img.width);
    const srcY = Math.round(cropArea.y * img.height);
    ctx.drawImage(
      img,
      srcX,
      srcY,
      cropSizePx,
      cropSizePx,
      0,
      0,
      outputSize,
      outputSize
    );

    // JPEG形式で圧縮（品質85%）- PNGより大幅にファイルサイズが小さくなる
    const base64 = cropCanvas.toDataURL('image/jpeg', 0.85).split(',')[1];
    onConfirm(base64);
  }, [cropArea, onConfirm]);

  return (
    <div className="face-crop-modal-overlay" onClick={onCancel}>
      <div className="face-crop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="face-crop-modal-header">
          <h3>顔の位置を調整</h3>
          <button className="face-crop-close-btn" onClick={onCancel}>
            ×
          </button>
        </div>

        <p className="face-crop-help">
          円形の枠をドラッグして顔の位置を調整してください
        </p>

        <div className="face-crop-canvas-container" ref={containerRef}>
          {imageDataUrl ? (
            <canvas
              ref={canvasRef}
              className="face-crop-canvas"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            />
          ) : (
            <div className="face-crop-loading">読み込み中...</div>
          )}
        </div>

        <div className="face-crop-modal-footer">
          <button className="face-crop-cancel-btn" onClick={onCancel}>
            キャンセル
          </button>
          <button className="face-crop-confirm-btn" onClick={handleConfirm}>
            この位置で確定
          </button>
        </div>
      </div>
    </div>
  );
}
